import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileCheck, Award, CheckCircle, Globe, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Niyukti = () => {
  const heroRef = useScrollAnimation({ threshold: 0.2 });
  const valueRef = useScrollAnimation({ threshold: 0.2 });
  const useCasesRef = useScrollAnimation({ threshold: 0.2 });
  const ctaRef = useScrollAnimation({ threshold: 0.3 });

  // Mobile detection for enhanced animations
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero */}
      <section className="bg-gradient-to-br from-cyan-50 to-teal-50 py-12 lg:py-16 overflow-hidden" ref={heroRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
            <div className="max-w-2xl">
              {/* Mobile: motion animation, Desktop: CSS transition */}
              {isMobile ? (
                <motion.div
                  className="inline-flex items-center justify-center p-3 bg-cyan-100 rounded-2xl mb-6"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "backOut" }}
                >
                  <Users className="text-cyan-600" size={32} />
                </motion.div>
              ) : (
                <div className={`inline-flex items-center justify-center p-3 bg-cyan-100 rounded-2xl mb-6 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
                  <Users className="text-cyan-600" size={32} />
                </div>
              )}

              {isMobile ? (
                <motion.h1
                  className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                >
                  Niyukti AI
                </motion.h1>
              ) : (
                <h1 className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: heroRef.isVisible ? '0.1s' : undefined }}>
                  Niyukti AI
                </h1>
              )}

              {isMobile ? (
                <motion.p
                  className="text-xl text-gray-600 mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  AI-powered application filtering and workflow tool designed to revolutionize modern recruitment.
                </motion.p>
              ) : (
                <p className={`text-xl text-gray-600 mb-8 leading-relaxed transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: heroRef.isVisible ? '0.2s' : undefined }}>
                  AI-powered application filtering and workflow tool designed to revolutionize modern recruitment.
                </p>
              )}

              {isMobile ? (
                <motion.div
                  className="flex flex-col sm:flex-row gap-4"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                >
                  <Link to="/contact" className="px-8 py-3 bg-cyan-600 text-white rounded-full font-semibold hover:bg-cyan-700 transition-colors text-center">
                    Request Demo
                  </Link>
                </motion.div>
              ) : (
                <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: heroRef.isVisible ? '0.3s' : undefined }}>
                  <Link to="/contact" className="px-8 py-3 bg-cyan-600 text-white rounded-full font-semibold hover:bg-cyan-700 transition-colors text-center">
                    Request Demo
                  </Link>
                </div>
              )}
            </div>
            {isMobile ? (
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                <div className="absolute inset-0 bg-cyan-600 rounded-3xl -rotate-3 opacity-10"></div>
                <img
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&q=80&w=2070"
                  alt="Professional Boardroom Meeting"
                  className="relative rounded-3xl shadow-2xl border-4 border-white"
                />
              </motion.div>
            ) : (
              <div className={`relative transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: heroRef.isVisible ? '0.4s' : undefined }}>
                <div className="absolute inset-0 bg-cyan-600 rounded-3xl -rotate-3 opacity-10"></div>
                <img
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&q=80&w=2070"
                  alt="Professional Boardroom Meeting"
                  className="relative rounded-3xl shadow-2xl border-4 border-white transform hover:translate-y-2 transition-transform duration-500"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Value Prop */}
      <section className="py-12 bg-white" ref={valueRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center">
            <div>
              {isMobile ? (
                <>
                  <motion.h2
                    className="text-3xl font-bold text-gray-900 mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    Recruitment, Reimagined
                  </motion.h2>
                  <motion.p
                    className="text-lg text-gray-600 mb-6 leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    Niyukti AI is the only platform with native support for India's complex reservation policies (UR/OBC/SC/ST/EWS). It combines enterprise-grade ATS features with advanced AI screening to reduce time-to-hire by 60-70%.
                  </motion.p>
                  <motion.ul
                    className="space-y-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <li className="flex items-center gap-3">
                      <div className="p-1 bg-green-100 rounded-full"><CheckCircle size={16} className="text-green-600" /></div>
                      <span className="text-gray-700">Native Reservation Policy Support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="p-1 bg-green-100 rounded-full"><CheckCircle size={16} className="text-green-600" /></div>
                      <span className="text-gray-700">Cloud-Agnostic (AWS, Azure, On-Prem)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="p-1 bg-green-100 rounded-full"><CheckCircle size={16} className="text-green-600" /></div>
                      <span className="text-gray-700">Gemini Pro Vision for Document OCR</span>
                    </li>
                  </motion.ul>
                </>
              ) : (
                <>
                  <h2 className={`text-3xl font-bold text-gray-900 mb-6 transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>Recruitment, Reimagined</h2>
                  <p className={`text-lg text-gray-600 mb-6 leading-relaxed transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: valueRef.isVisible ? '0.1s' : undefined }}>
                    Niyukti AI is the only platform with native support for India's complex reservation policies (UR/OBC/SC/ST/EWS). It combines enterprise-grade ATS features with advanced AI screening to reduce time-to-hire by 60-70%.
                  </p>
                  <ul className={`space-y-4 transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: valueRef.isVisible ? '0.2s' : undefined }}>
                    <li className="flex items-center gap-3">
                      <div className="p-1 bg-green-100 rounded-full"><CheckCircle size={16} className="text-green-600" /></div>
                      <span className="text-gray-700">Native Reservation Policy Support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="p-1 bg-green-100 rounded-full"><CheckCircle size={16} className="text-green-600" /></div>
                      <span className="text-gray-700">Cloud-Agnostic (AWS, Azure, On-Prem)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="p-1 bg-green-100 rounded-full"><CheckCircle size={16} className="text-green-600" /></div>
                      <span className="text-gray-700">Gemini Pro Vision for Document OCR</span>
                    </li>
                  </ul>
                </>
              )}
            </div>
            {isMobile ? (
              <motion.div
                className="bg-gray-50 p-8 rounded-3xl border border-gray-100"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <motion.div
                    className="bg-white p-6 rounded-2xl shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="text-3xl font-bold text-cyan-600 mb-1">60%</div>
                    <div className="text-sm text-gray-500">Faster Hiring</div>
                  </motion.div>
                  <motion.div
                    className="bg-white p-6 rounded-2xl shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="text-3xl font-bold text-cyan-600 mb-1">100%</div>
                    <div className="text-sm text-gray-500">Compliance</div>
                  </motion.div>
                  <motion.div
                    className="bg-white p-6 rounded-2xl shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="text-3xl font-bold text-cyan-600 mb-1">3x</div>
                    <div className="text-sm text-gray-500">Recruiter Productivity</div>
                  </motion.div>
                  <motion.div
                    className="bg-white p-6 rounded-2xl shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <div className="text-3xl font-bold text-cyan-600 mb-1">ZERO</div>
                    <div className="text-sm text-gray-500">Manual Screening</div>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              <div className={`bg-gray-50 p-8 rounded-3xl border border-gray-100 transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: valueRef.isVisible ? '0.3s' : undefined }}>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`bg-white p-6 rounded-2xl shadow-sm transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: valueRef.isVisible ? '0.4s' : undefined }}>
                    <div className="text-3xl font-bold text-cyan-600 mb-1">60%</div>
                    <div className="text-sm text-gray-500">Faster Hiring</div>
                  </div>
                  <div className={`bg-white p-6 rounded-2xl shadow-sm transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: valueRef.isVisible ? '0.5s' : undefined }}>
                    <div className="text-3xl font-bold text-cyan-600 mb-1">100%</div>
                    <div className="text-sm text-gray-500">Compliance</div>
                  </div>
                  <div className={`bg-white p-6 rounded-2xl shadow-sm transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: valueRef.isVisible ? '0.6s' : undefined }}>
                    <div className="text-3xl font-bold text-cyan-600 mb-1">3x</div>
                    <div className="text-sm text-gray-500">Recruiter Productivity</div>
                  </div>
                  <div className={`bg-white p-6 rounded-2xl shadow-sm transition-all duration-1000 ${valueRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: valueRef.isVisible ? '0.7s' : undefined }}>
                    <div className="text-3xl font-bold text-cyan-600 mb-1">ZERO</div>
                    <div className="text-sm text-gray-500">Manual Screening</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Key Use Cases */}
      <section className="py-24 bg-gray-50" ref={useCasesRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            {isMobile ? (
              <motion.h2
                className="text-3xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Built for Every Industry
              </motion.h2>
            ) : (
              <h2 className={`text-3xl font-bold text-gray-900 mb-4 transition-all duration-1000 ${useCasesRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>Built for Every Industry</h2>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {isMobile ? (
              <>
                <motion.div
                  className="bg-white p-8 rounded-2xl shadow-sm border border-transparent"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-6">
                    <Building2 size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Government & PSU</h3>
                  <p className="text-gray-600">Strict adherence to reservation quotas, audit trails, and mass recruitment capabilities for UPSC, RRB, and State PSCs.</p>
                </motion.div>
                <motion.div
                  className="bg-white p-8 rounded-2xl shadow-sm border border-transparent"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                    <Globe size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">IT Services</h3>
                  <p className="text-gray-600">High-volume campus hiring, skills-based AI matching, and seamless offer management for global tech giants.</p>
                </motion.div>
                <motion.div
                  className="bg-white p-8 rounded-2xl shadow-sm border border-transparent"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-6">
                    <FileCheck size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Healthcare</h3>
                  <p className="text-gray-600">Credential verification, license tracking, and specialized hiring workflows for hospitals and medical institutions.</p>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`bg-white p-8 rounded-2xl shadow-sm border border-transparent hover:border-orange-300 transition-all duration-300 ${useCasesRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: useCasesRef.isVisible ? '0.1s' : undefined }}>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-6">
                    <Building2 size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Government & PSU</h3>
                  <p className="text-gray-600">Strict adherence to reservation quotas, audit trails, and mass recruitment capabilities for UPSC, RRB, and State PSCs.</p>
                </div>
                <div className={`bg-white p-8 rounded-2xl shadow-sm border border-transparent hover:border-blue-300 transition-all duration-300 ${useCasesRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: useCasesRef.isVisible ? '0.2s' : undefined }}>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                    <Globe size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">IT Services</h3>
                  <p className="text-gray-600">High-volume campus hiring, skills-based AI matching, and seamless offer management for global tech giants.</p>
                </div>
                <div className={`bg-white p-8 rounded-2xl shadow-sm border border-transparent hover:border-green-300 transition-all duration-300 ${useCasesRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: useCasesRef.isVisible ? '0.3s' : undefined }}>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-6">
                    <FileCheck size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Healthcare</h3>
                  <p className="text-gray-600">Credential verification, license tracking, and specialized hiring workflows for hospitals and medical institutions.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

       {/* Pricing CTA */}
       <section className="py-20 bg-cyan-900 text-white relative overflow-hidden" ref={ctaRef.ref}>
        {isMobile ? (
          <motion.div
            className="absolute inset-0 opacity-20"
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            style={{
              backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.3) 0%, transparent 60%)',
              backgroundSize: '200% 200%',
            }}
          />
        ) : null}
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          {isMobile ? (
            <>
              <motion.h2
                className="text-3xl font-bold mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Start Hiring Smarter Today
              </motion.h2>
              <motion.p
                className="text-xl text-cyan-100 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Flexible pricing models: SaaS subscription or Perpetual On-Premise License.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "backOut" }}
                viewport={{ once: true }}
              >
                <motion.div
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="inline-block px-8 py-3 bg-white text-cyan-900 rounded-full font-bold hover:bg-cyan-50 transition-all"
                  >
                    Get a Quote
                  </Link>
                </motion.div>
              </motion.div>
            </>
          ) : (
            <>
              <h2 className={`text-3xl font-bold mb-6 transition-all duration-1000 ${ctaRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>Start Hiring Smarter Today</h2>
              <p className={`text-xl text-cyan-100 mb-8 transition-all duration-1000 ${ctaRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: ctaRef.isVisible ? '0.1s' : undefined }}>
                Flexible pricing models: SaaS subscription or Perpetual On-Premise License.
              </p>
              <Link
                to="/contact"
                className={`inline-block px-8 py-3 bg-white text-cyan-900 rounded-full font-bold hover:bg-cyan-50 transition-all duration-1000 ${ctaRef.isVisible ? 'opacity-100' : 'opacity-0'}`}
                style={{ transitionDelay: ctaRef.isVisible ? '0.2s' : undefined }}
              >
                Get a Quote
              </Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Niyukti;
